/* istanbul ignore file */
/* eslint-disable max-lines */

import {StyleType, CssColor, wixColor} from '@wix/editor-platform-ooi-metadata-resolver';
import {createStylesParam, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';
import {AddToCartActionOption} from '@wix/wixstores-client-core/dist/es/src/constants';
import {
  AddToCartFlowType,
  AutoGridTemplateRepeatOptions,
  CategoryHeaderLayout,
  CategoryHeaderPosition,
  CategoryListHorizontalButtonBorderType,
  CategoryListHorizontalOverflow,
  CategoryListHorizontalPosition,
  CategoryListItemsToShow,
  CategoryListLayout,
  CategoryListStrategy,
  DEFAULT_AUTO_GRID_PRODUCTS_COUNT,
  DEFAULT_COLS,
  DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
  DEFAULT_PRODUCT_IMAGE_SIZE,
  DEFAULT_ROWS,
  FilterAndSortButtonStyle,
  MAX_ROWS,
  ProductBorderStyleType,
  ScrollNavigationIntervalsOption,
  SliderArrowsPlacement,
  TextualOptionType,
} from '../constants';
import {
  AddToCartButtonIcon,
  AddToCartButtonPlacement,
  Alignments,
  ButtonContentType,
  ButtonWidthType,
  CategoryListHeaderBehavior,
  ContentJustification,
  GridType,
  HoverType,
  ImageCarouselArrowSize,
  ImageModeId,
  ImagePlacements,
  ImagePositions,
  ImageRatioId,
  LoadMoreType,
  LongCategoryListLayout,
  NamePriceLayout,
  PaginationType,
  PriceAndDiscountLayouts,
  RibbonPlacementId,
  RibbonStyle,
  RibbonType,
  FooterDescriptionTrimMode,
  VerticalAlignments,
  ProductOptionsDisplayOption,
} from '../types/galleryTypes';
import {GalleryViewMode} from '../wishlist/constants';
import {IStylesParams} from './types';
import {getFlexAlignment} from '../common/getFlexAlignment';

const responsive = createStylesParam('responsive', {
  type: StyleParamType.Boolean,
  getDefaultValue: ({isEditorX}) => isEditorX,
});

const showAlternativeImage = createStylesParam('showAlternativeImage', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showProductOptionsButton = createStylesParam('gallery_showProductOptionsButton', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const galleryGridType = createStylesParam('gallery_gridType', {
  type: StyleParamType.Number,
  getDefaultValue: ({getStyleParamValue}) => (getStyleParamValue(responsive) ? GridType.AUTO : GridType.MANUAL),
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description:
        'controls gallery grid type: manual - fixed number of columns (1) or auto - optimized the grid based on the screen size (2)',
    },
    defaultValue: {
      type: 'static',
      value: GridType.AUTO,
    },
  },
});

export const galleryColumnsDefaultValue = ({dimensions, isMobile}) => {
  if (isMobile) {
    return 1;
  }

  if (!dimensions) {
    return;
  }

  if (typeof dimensions.width !== 'number') {
    return DEFAULT_COLS;
  }
  const componentWidth: number = dimensions.width;
  const PRODUCT_ITEM_MIN_WIDTH: number = 250;
  const defaultColumns = Math.round(componentWidth / PRODUCT_ITEM_MIN_WIDTH);
  return Math.min(defaultColumns, DEFAULT_COLS);
};

const galleryColumns = createStylesParam('galleryColumns', {
  type: StyleParamType.Number,
  getDefaultValue: galleryColumnsDefaultValue,
  metadata: {
    type: StyleType.number,
    defaultValue: {
      type: 'static',
      value: DEFAULT_COLS,
    },
    mobileDefaultValue: {
      type: 'static',
      value: 1,
    },
  },
});

export const galleryRowsDefaultValue = ({getStyleParamValue}) => {
  const columnsNumber = getStyleParamValue(galleryColumns);
  if (!columnsNumber) {
    return;
  }
  const LEGACY_MAX_ITEM_PER_PAGE = 20;
  return Math.min(Math.floor(LEGACY_MAX_ITEM_PER_PAGE / columnsNumber), MAX_ROWS / 2);
};

const galleryMargin = createStylesParam('galleryMargin', {
  type: StyleParamType.Number,
  getDefaultValue: () => 10,
  metadata: {
    type: StyleType.margin,
    defaultValue: {
      type: 'static',
      value: 10,
    },
  },
});

const gallery_showColorOptionsOnly = createStylesParam('gallery_showColorOptionsOnly', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showProductName = createStylesParam('gallery_showProductName', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showDividers = createStylesParam('gallery_showDividers', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showPrice = createStylesParam('gallery_showPrice', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showDiscountName = createStylesParam('gallery_showDiscountName', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showRibbon = createStylesParam('gallery_showRibbon', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showDigitalBadge = createStylesParam('gallery_showDigitalBadge', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_productMargin = createStylesParam('gallery_productMargin', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
  metadata: {
    type: StyleType.margin,
    defaultValue: {
      type: 'static',
      value: 0,
    },
  },
});

const gallery_gapSize = createStylesParam('gallery_gapSize', {
  type: StyleParamType.Number,
  getDefaultValue: () => 20,
  metadata: {
    type: StyleType.gap,
    defaultValue: {
      type: 'static',
      value: 20,
    },
  },
});

const gallery_fixedGridProductsCount = createStylesParam('gallery_fixedGridProductsCount', {
  type: StyleParamType.Number,
  getDefaultValue: () => DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
  metadata: {
    type: StyleType.number,
    ai: {
      description: 'controls the number of products shown in each gallery page',
    },
    defaultValue: {
      type: 'static',
      value: DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
    },
  },
});

const gallery_imageMode = createStylesParam('gallery_imageMode', {
  type: StyleParamType.Number,
  getDefaultValue: () => ImageModeId.Crop,
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description: 'controls how product images are resized: crop to fill (1) or fit within bounds (2)',
    },
    defaultValue: {
      type: 'static',
      value: ImageModeId.Crop,
    },
  },
});

const galleryImageRatio = createStylesParam('galleryImageRatio', {
  type: StyleParamType.Number,
  getDefaultValue: () => ImageRatioId._1x1,
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description:
        'controls product image aspect ratio: 3:2 (0), 4:3 (1), 1:1 (2), 3:4 (3), 2:3 (4), 16:9 (5), or 9:16 (6)',
    },
    defaultValue: {
      type: 'static',
      value: ImageRatioId._1x1,
    },
  },
});

const gallery_imageWidth = createStylesParam('gallery_imageWidth', {
  type: StyleParamType.Number,
  getDefaultValue: () => 50,
  metadata: {
    type: StyleType.width,
    defaultValue: {
      type: 'static',
      value: 50,
    },
  },
});

const gallery_imageAndInfoSpacing = createStylesParam('gallery_imageAndInfoSpacing', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
  metadata: {
    type: StyleType.gap,
    defaultValue: {
      type: 'static',
      value: 0,
    },
  },
});

const gallery_alternateImagePosition = createStylesParam('gallery_alternateImagePosition', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showAddToCartQuantity = createStylesParam('gallery_showAddToCartQuantity', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showAddToCartButton = createStylesParam('gallery_showAddToCartButton', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_titleFontStyle = createStylesParam('gallery_titleFontStyle', {
  type: StyleParamType.Font,
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_priceFontStyle = createStylesParam('gallery_priceFontStyle', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_discountNameFont = createStylesParam('gallery_discountNameFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_addToCartButtonTextFont = createStylesParam('gallery_addToCartButtonTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 15,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 15,
    },
  },
});

const gallery_ribbonTextFont = createStylesParam('gallery_ribbonTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_hoverType = createStylesParam('gallery_hoverType', {
  type: StyleParamType.Font,
  getDefaultValue: ({getStyleParamValue}) => ({
    fontStyleParam: false,
    value: getStyleParamValue(showAlternativeImage) ? HoverType.Alternate : HoverType.None,
  }),
  metadata: {
    type: StyleType.legacyFontStyleParamEnum,
    defaultValue: {
      type: 'static',
      value: HoverType.None,
    },
  },
});

const gallery_categoryHeaderLayout = createStylesParam('gallery_categoryHeaderLayout', {
  type: StyleParamType.Number,
  getDefaultValue: () => CategoryHeaderLayout.TextBelow,
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description:
        'controls category header layout: text below image (0), text inside image (1), or text above image (2)',
    },
    defaultValue: {
      type: 'static',
      value: CategoryHeaderLayout.TextBelow,
    },
  },
});

const gallery_imagePosition = createStylesParam('gallery_imagePosition', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: ImagePositions.LEFT,
  }),
  metadata: {
    type: StyleType.legacyFontStyleParamEnum,
    defaultValue: {
      type: 'static',
      value: ImagePositions.LEFT,
    },
  },
});

const gallery_imagePlacement = createStylesParam('gallery_imagePlacement', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: ImagePlacements.VERTICAL,
  }),
  metadata: {
    type: StyleType.legacyFontStyleParamEnum,
    defaultValue: {
      type: 'static',
      value: ImagePlacements.VERTICAL,
    },
  },
});

const gallery_categoriesBreadcrumbsFont = createStylesParam('gallery_categoriesBreadcrumbsFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryNameFont = createStylesParam('gallery_categoryNameFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 28,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 28,
    },
  },
});

const gallery_categoryDescriptionFont = createStylesParam('gallery_categoryDescriptionFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryDescriptionReadMoreLinkFont = createStylesParam('gallery_categoryDescriptionReadMoreLinkFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryProductCounterFont = createStylesParam('gallery_categoryProductCounterFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_categoryProductCounterColor = createStylesParam('gallery_categoryProductCounterColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-4'),
  metadata: {
    type: StyleType.color,
    defaultValue: wixColor(CssColor.WstParagraph3Color),
  },
});

const gallery_showCategoriesProductsCounter = createStylesParam('gallery_showCategoriesProductsCounter', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_filterOptionsFont = createStylesParam('gallery_filterOptionsFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_arrowBackground = createStylesParam('gallery_arrowBackground', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: wixColor(CssColor.WstParagraph2Color),
  },
});

const gallery_categoryListLayout = createStylesParam('gallery_categoryListLayout', {
  type: StyleParamType.Number,
  inheritDesktop: false,
  getDefaultValue: ({isMobile}) => (isMobile ? CategoryListLayout.Horizontal : CategoryListLayout.Vertical),
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description: 'controls category list layout: vertical list (0) or horizontal buttons (1)',
    },
    defaultValue: {
      type: 'static',
      value: CategoryListLayout.Vertical,
    },
    mobileDefaultValue: {
      type: 'static',
      value: CategoryListLayout.Horizontal,
    },
  },
});

const gallery_categoryListHorizontalBaseButtonBorderWidth = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonBorderWidth',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }
);

const gallery_categoryListHorizontalBaseButtonCornerRadius = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonCornerRadius',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }
);

const gallery_categoryListHorizontalBaseButtonBorderType = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonBorderType',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalButtonBorderType.Full,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls horizontal category list button border type: none (0), bottom only (1), or all sides (2)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListHorizontalButtonBorderType.Full,
      },
    },
  }
);

const gallery_priceColor = createStylesParam('gallery_priceColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-4'),
  metadata: {
    type: StyleType.color,
    defaultValue: wixColor(CssColor.WstParagraph3Color),
  },
});

const gallery_categoryListHorizontalBaseButtonTextFont = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonTextFont',
  {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: false,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: false,
        // },
      },
    },
  }
);

const gallery_headerTextFont = createStylesParam('gallery_headerTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 25,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 25,
    },
  },
});

const gallery_categoryTreeTitleFont = createStylesParam('gallery_categoryTreeTitleFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 20,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 20,
    },
  },
});

const gallery_categoryTreeTitleColor = createStylesParam('gallery_categoryTreeTitleColor', {
  type: StyleParamType.Color,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.color,
    defaultValue: wixColor(CssColor.WstParagraph2Color),
  },
});

const gallery_sortingFiltersTextColor = createStylesParam('gallery_sortingFiltersTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
  metadata: {
    type: StyleType.color,
    defaultValue: wixColor(CssColor.WstParagraph2Color),
  },
});

const gallery_outOfStockFontStyle = createStylesParam('gallery_outOfStockFontStyle', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_quantityAndOptionsBackgroundColor = createStylesParam('gallery_quantityAndOptionsBackgroundColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-1'),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
  },
});

const gallery_quantityAndOptionsTextColor = createStylesParam('gallery_quantityAndOptionsTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.color,
    defaultValue: wixColor(CssColor.WstParagraph2Color),
  },
});

const gallery_quantityAndOptionsBorderWidth = createStylesParam('gallery_quantityAndOptionsBorderWidth', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: '1px',
  }),
  metadata: {
    type: StyleType.borderWidth,
    defaultValue: {
      type: 'static',
      value: 1,
    },
  },
});

const gallery_quantityAndOptionsBorderColor = createStylesParam('gallery_quantityAndOptionsBorderColor', {
  type: StyleParamType.Color,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.borderColor,
    defaultValue: wixColor(CssColor.WstParagraph2Color),
  },
});

const gallery_quantityAndOptionsColorOptionsSize = createStylesParam('gallery_quantityAndOptionsColorOptionsSize', {
  type: StyleParamType.Number,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.number,
    defaultValue: {
      type: 'static',
      value: 24,
    },
  },
});

const gallery_quantityAndOptionsColorCornerRadius = createStylesParam('gallery_quantityAndOptionsColorCornerRadius', {
  type: StyleParamType.Number,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.borderRadius,
    defaultValue: {
      type: 'static',
      value: 12,
    },
  },
});

const gallery_quantityAndOptionsTextFont = createStylesParam('gallery_quantityAndOptionsTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_quantityAndOptionsAdditionalSwatchesTextFont = createStylesParam(
  'gallery_quantityAndOptionsAdditionalSwatchesTextFont',
  {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  }
);

const gallery_quantityAndOptionsAdditionalSwatchesTextColor = createStylesParam(
  'gallery_quantityAndOptionsAdditionalSwatchesTextColor',
  {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph3Color),
    },
  }
);

const gallery_addToCartButtonPlacement = createStylesParam('gallery_addToCartButtonPlacement', {
  type: StyleParamType.Number,
  getDefaultValue: () => AddToCartButtonPlacement.ProductInfo,
  metadata: {
    type: StyleType.numberEnum,
    ai: {
      description: 'controls add to cart button placement: on image (1) or in product info section (2)',
    },
    defaultValue: {
      type: 'static',
      value: AddToCartButtonPlacement.ProductInfo,
    },
  },
});

const gallery_alignment = createStylesParam('gallery_alignment', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: Alignments.LEFT,
  }),
  metadata: {
    type: StyleType.legacyFontStyleParamEnum,
    defaultValue: {
      type: 'static',
      value: Alignments.LEFT,
    },
  },
});

export const baseStylesParams: IStylesParams = {
  gallery_discountNameFont,
  gallery_addToCartButtonTextFont,
  gallery_ribbonTextFont,
  gallery_priceFontStyle,
  full_width: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },
  gallery_productBorderStyleType: {
    type: StyleParamType.Number,
    getDefaultValue: () => ProductBorderStyleType.INLINE,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls product border style: inline border (1) or outline border (2)',
      },
      defaultValue: {
        type: 'static',
        value: ProductBorderStyleType.INLINE,
      },
    },
  },
  showAlternativeImage,
  gallery_showColorOptionsOnly,
  gallery_paginationDotsBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_paginationDotsOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  galleryFiltersCategories: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryFiltersPrice: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryFiltersProductOptions: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryShowFilters: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  galleryShowSort: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallerySortNameAsc: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortNameDes: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortNewest: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortPriceAsc: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortPriceDes: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortRecommended: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_priceAndDiscountLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => PriceAndDiscountLayouts.HORIZONTAL_REGULAR_PRICE_AFTER_DISCOUNT,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls price and discount layout: horizontal regular price before discount (0), horizontal regular price after discount (1), vertical regular price before discount (2), or vertical regular price after discount (3)',
      },
      defaultValue: {
        type: 'static',
        value: PriceAndDiscountLayouts.HORIZONTAL_REGULAR_PRICE_AFTER_DISCOUNT,
      },
    },
  },
  gallery_showAddToCartButton,
  'mobile:gallery_showAddToCartButton': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showAddToCartButton),
  },
  gallery_addToCartButtonContentType: {
    type: StyleParamType.Number,
    getDefaultValue: () => ButtonContentType.Text,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls add to cart button content: text only (1), icon and text (2), or icon only (3)',
      },
      defaultValue: {
        type: 'static',
        value: ButtonContentType.Text,
      },
    },
  },
  gallery_addToCartButtonIcon: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartButtonIcon.Plus,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls add to cart button icon: narrow shopping bag (1), narrow filled shopping bag (2), plus (3), rounded square shopping bag (4), tall square shopping bag (5), shopping cart slanted without the top (6), square shopping cart with top (7), or filled square shopping cart with top (8)',
      },
      defaultValue: {
        type: 'static',
        value: AddToCartButtonIcon.Plus,
      },
    },
  },
  gallery_addToCartButtonIconSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 30,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 30,
      },
    },
  },
  gallery_addToCartButtonPlacement,
  gallery_addToCartButtonOnImageSidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_addToCartButtonOnImageBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.paddingBottom,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_addToCartButtonShowOnHover: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_alternateImagePosition,
  'mobile:gallery_alternateImagePosition': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_alternateImagePosition),
  },
  gallery_showAddToCartQuantity,
  'mobile:gallery_showQuantity': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showAddToCartQuantity),
  },
  gallery_showDividers,
  gallery_showDigitalBadge,
  gallery_showPrice,
  gallery_showDiscountName,
  gallery_showRibbon,
  gallery_ribbonSidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonTopPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.paddingTop,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_namePriceLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => NamePriceLayout.NameAbovePrice,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls product name and price layout: name above price (1) or name and price side by side (2)',
      },
      defaultValue: {
        type: 'static',
        value: NamePriceLayout.NameAbovePrice,
      },
    },
  },
  gallery_showProductName,
  gallery_showTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showCategoryHeaderSection: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderImage: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({isMobile}) => !isMobile,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      mobileDefaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showCategoryHeaderReadMoreLink: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderName: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderDescription: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({isMobile}) => !isMobile,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      mobileDefaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_paginationFirstLastArrows: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showProductOptionsButton,
  'mobile:gallery_showProductOptions': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showProductOptionsButton),
  },
  gallery_productOptionsDisplayOption: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => {
      const showColorOptionsOnly = getStyleParamValue(gallery_showColorOptionsOnly);
      return showColorOptionsOnly
        ? ProductOptionsDisplayOption.FIRST_COLOR
        : ProductOptionsDisplayOption.FIRST_TWO_OPTIONS;
    },
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls which product options to display: two options (0) or first option of any type (1) or color option only (2)',
      },
      defaultValue: {
        type: 'static',
        value: ProductOptionsDisplayOption.FIRST_TWO_OPTIONS,
      },
    },
  },
  gallery_shouldLimitProductOptions: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_productOptionSwatchesLimit: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_productDropdownListWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  gallery_quantityBoxWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  showQuickView: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  responsive,
  galleryColumns,
  galleryRows: {
    type: StyleParamType.Number,
    getDefaultValue: galleryRowsDefaultValue,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: DEFAULT_ROWS,
      },
    },
  },
  galleryMargin,
  galleryMarginRow: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryMargin),
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'ref',
        ref: 'galleryMargin',
      },
    },
  },
  galleryMarginColumn: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryMargin),
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'ref',
        ref: 'galleryMargin',
      },
    },
  },
  'mobile:galleryMarginRow': {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  'mobile:galleryMarginColumn': {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  gallery_productMargin,
  'mobile:gallery_productMargin': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_productMargin) ?? 0,
  },
  gallery_imageMode,
  'mobile:gallery_imageMode': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageMode),
  },
  gallery_ribbonPlacement: {
    type: StyleParamType.Number,
    getDefaultValue: () => RibbonPlacementId.OnImage,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls product ribbon placement: on image (1) or in product info section (2)',
      },
      defaultValue: {
        type: 'static',
        value: RibbonPlacementId.OnImage,
      },
    },
  },
  gallery_ribbonType: {
    type: StyleParamType.Number,
    getDefaultValue: () => RibbonType.RECTANGLE,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls product ribbon type: rectangle (1) or text only (2)',
      },
      defaultValue: {
        type: 'static',
        value: RibbonType.RECTANGLE,
      },
    },
  },
  gallery_ribbonStyle: {
    type: StyleParamType.Number,
    getDefaultValue: () => RibbonStyle.RECTANGLE,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls product ribbon style: rectangle (1), rectangle outline (2), pill (3), pill outline (4), or text only (5)',
      },
      defaultValue: {
        type: 'static',
        value: RibbonStyle.RECTANGLE,
      },
    },
  },
  galleryImageRatio,
  'mobile:galleryImageRatio': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryImageRatio),
  },
  'mobile:galleryMargin': {
    type: StyleParamType.Number,
    getDefaultValue: () => 10,
  },
  'mobile:galleryColumns': {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  gallery_addToCartAction: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartActionOption.MINI_CART,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls add to cart action behavior: success popup (0), open mini cart (1), navigate to cart page (2), or stay on product page (3)',
      },
      defaultValue: {
        type: 'static',
        value: AddToCartActionOption.MINI_CART,
      },
    },
  },
  gallery_productSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => DEFAULT_PRODUCT_IMAGE_SIZE,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: DEFAULT_PRODUCT_IMAGE_SIZE,
      },
    },
  },
  gallery_productsCount: {
    type: StyleParamType.Number,
    getDefaultValue: () => DEFAULT_AUTO_GRID_PRODUCTS_COUNT,
    metadata: {
      type: StyleType.number,
      ai: {
        description: 'controls the number of products shown in each gallery page',
      },
      defaultValue: {
        type: 'static',
        value: DEFAULT_AUTO_GRID_PRODUCTS_COUNT,
      },
    },
  },
  gallery_fixedGridProductsCount,
  gallery_gapSize,
  gallery_gapSizeColumn: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_gapSize),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_gapSize',
      },
    },
  },
  gallery_gapSizeRow: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_gapSize),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_gapSize',
      },
    },
  },
  gallery_gridType: galleryGridType,
  gallery_loadMoreProductsType: {
    type: StyleParamType.Number,
    getDefaultValue: () => LoadMoreType.BUTTON,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls how more products are loaded: load more button (1), pagination (2), or infinite scroll (3)',
      },
      defaultValue: {
        type: 'static',
        value: LoadMoreType.BUTTON,
      },
    },
  },
  gallery_loadMoreButtontextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_loadMoreButtonborderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_paginationFormat: {
    type: StyleParamType.Number,
    getDefaultValue: () => PaginationType.PAGES,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls pagination format: pages (1) or compact (2)',
      },
      defaultValue: {
        type: 'static',
        value: PaginationType.PAGES,
      },
    },
  },
  gallery_titleFontStyle,
  'mobile:gallery_headerTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_headerTextFont)?.size ?? 25,
  },
  'mobile:gallery_productNameFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_titleFontStyle)?.size ?? 16,
  },
  'mobile:gallery_productPriceFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle)?.size ?? 16,
  },
  gallery_strikethroughPriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceFontStyle',
      },
    },
  },
  gallery_salePriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceFontStyle',
      },
    },
  },
  'mobile:gallery_discountNameFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_discountNameFont)?.size ?? 14,
  },
  'mobile:gallery_buttonTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_addToCartButtonTextFont)?.size ?? 15,
  },
  'mobile:gallery_ribbonTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_ribbonTextFont)?.size ?? 14,
  },
  gallery_imageWidth,
  'mobile:gallery_imageWidth': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageWidth),
  },
  gallery_imageAndInfoSpacing,
  'mobile:gallery_imageAndInfoSpacing': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageAndInfoSpacing),
  },
  gallery_itemLinkVerticalCustomizePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_itemLinkHorizontalCustomizePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  'mobile:gallery_itemLinkVerticalCustomizePadding': {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  'mobile:gallery_itemLinkHorizontalCustomizePadding': {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  gallery_categoryHeaderSpaceBetweenTitleDescription: {
    type: StyleParamType.Number,
    getDefaultValue: ({isMobile}) => (isMobile ? 0 : 4),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 4,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryHeaderImageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryDescriptionContainerCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryHeaderPosition: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryHeaderPosition.MinimizedAboveGallery,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls category header position: iniline with products gallery (0) or stretched at top (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryHeaderPosition.MinimizedAboveGallery,
      },
    },
  },
  gallery_categoryHeaderLayout,
  gallery_categoryHeaderImageMinHeight: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: ({isMobile, getStyleParamValue}) => {
      if (isMobile) {
        return getStyleParamValue(gallery_categoryHeaderLayout) === CategoryHeaderLayout.TextInside ? 304 : 140;
      }
      return 280;
    },
    metadata: {
      type: StyleType.height,
      defaultValue: {
        type: 'static',
        value: 280,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 140, // TODO: editor 3 - verify required heights
      },
    },
  },
  gallery_categoryDescriptionContainerBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_hoverType,
  'mobile:gallery_imageEffect': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_hoverType),
  },
  gallery_imageCarouselArrowSize: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ImageCarouselArrowSize.SMALL,
    }),
  },
  gallery_paginationAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ContentJustification.CENTER,
    }),
  },
  gallery_verticalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: VerticalAlignments.CENTER,
    }),
  },
  gallery_imagePlacement,
  'mobile:gallery_imagePlacement': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imagePlacement),
  },
  gallery_imagePosition,
  'mobile:gallery_imagePosition': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imagePosition),
  },
  gallery_autoGridTemplateRepeatOption: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: AutoGridTemplateRepeatOptions.AUTO_FIT,
    }),
  },
  gallery_alignment,
  gallery_categoryInfoAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: ({isRTL}) => ({
      fontStyleParam: false,
      value: isRTL ? Alignments.RIGHT : Alignments.LEFT,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.LEFT,
      },
    },
  },
  gallery_categoryInfoVerticalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: VerticalAlignments.BOTTOM,
    }),
  },
  gallery_categoriesBreadcrumbsAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: ({isRTL}) => ({
      fontStyleParam: false,
      value: isRTL ? Alignments.RIGHT : Alignments.LEFT,
    }),
  },
  gallery_categoriesBreadcrumbsFont,
  gallery_showCategoriesBreadcrumbs: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryListHeader: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_categoryListHeaderBehavior: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHeaderBehavior.GO_TO_PARENT,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls the click action on the current category: navigate back to parent category (0) or do nothing (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListHeaderBehavior.GO_TO_PARENT,
      },
    },
  },
  gallery_longCategoryListLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => LongCategoryListLayout.SHOW_ALL,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls long category list collapsed state: show all categories (0) or limit visible categories (1)',
      },
      defaultValue: {
        type: 'static',
        value: LongCategoryListLayout.SHOW_ALL,
      },
    },
  },
  gallery_categoryListMaxRows: {
    type: StyleParamType.Number,
    getDefaultValue: () => 10,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 10,
      },
    },
  },
  gallery_showCategoriesProductsCounter,
  gallery_showProductsCounter: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showCategoriesProductsCounter),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_showCategoriesProductsCounter',
      },
    },
  },
  gallery_showAppliedFilters: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_appliedFiltersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({isMobile}) =>
      wixFontParam('Body-M', {
        size: isMobile ? 12 : 14,
      }) as any,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
      mobileDefaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 12,
      },
    },
  },
  gallery_addToCartButtonWidth: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ButtonWidthType.STRETCH,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: ButtonWidthType.STRETCH,
      },
    },
  },
  gallery_addToCartButtonAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.CENTER,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.CENTER,
      },
    },
  },
  gallery_addToCartButtonBorderSize: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '0px',
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: '0px',
      },
    },
  },
  gallery_addToCartButtonCornersRadius: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '0px',
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_addToCartButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryProductCounterFont,
  gallery_productsCounterFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryProductCounterFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryProductCounterFont',
      },
    },
  },
  gallery_categoryNameFont,
  gallery_categoryDescriptionFont,
  gallery_categoryDescriptionReadMoreLinkFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoryProductCounterColor,
  gallery_productsCounterColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryProductCounterColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryProductCounterColor',
      },
    },
  },
  gallery_categoriesBreadcrumbsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryDescriptionReadMoreLinkColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryDescriptionContainerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_categoryDescriptionContainerBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  // Footer Description - Visibility
  gallery_showCategoryFooterDescription: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },

  // Footer Description - Typography
  gallery_categoryFooterDescriptionHeading1Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-L'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Heading-L',
      },
    },
  },
  gallery_categoryFooterDescriptionHeading2Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Heading-M',
      },
    },
  },
  gallery_categoryFooterDescriptionHeading3Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-S'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Heading-S',
      },
    },
  },
  gallery_categoryFooterDescriptionHeading4Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-L'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Body-L',
      },
    },
  },
  gallery_categoryFooterDescriptionHeading5Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Body-M',
      },
    },
  },
  gallery_categoryFooterDescriptionHeading6Font: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Body-S',
      },
    },
  },
  gallery_categoryFooterDescriptionParagraphFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Body-M',
      },
    },
  },
  gallery_categoryFooterDescriptionQuoteFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'Body-M',
      },
    },
  },
  gallery_categoryFooterDescriptionAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.LEFT,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.LEFT,
      },
    },
  },

  // Footer Description - Colors
  gallery_categoryFooterDescriptionHeading1Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionHeading2Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionHeading3Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionHeading4Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionHeading5Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionHeading6Color: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionParagraphColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionQuoteColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },

  gallery_categoryFooterDescriptionTrimContent: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_categoryFooterDescriptionTrimMode: {
    type: StyleParamType.Number,
    getDefaultValue: () => FooterDescriptionTrimMode.Elements,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls footer description trim mode: elements (0) or words (1)',
      },
      defaultValue: {
        type: 'static',
        value: FooterDescriptionTrimMode.Elements,
      },
    },
  },
  gallery_categoryFooterDescriptionParagraphLimit: {
    type: StyleParamType.Number,
    getDefaultValue: () => 3,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 3,
      },
    },
  },
  gallery_categoryFooterDescriptionWordLimit: {
    type: StyleParamType.Number,
    getDefaultValue: () => 150,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 150,
      },
    },
  },

  gallery_categoryFooterDescriptionTopPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryFooterDescriptionBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryFooterDescriptionSidesPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },

  // Footer Description - Background & Border
  gallery_categoryFooterDescriptionBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_categoryFooterDescriptionBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryFooterDescriptionBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryFooterDescriptionBorderWeight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },

  'mobile:gallery_categoriesBreadcrumbsFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoriesBreadcrumbsFont)?.size ?? 16,
  },
  'mobile:gallery_categoryDescriptionFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryDescriptionFont)?.size ?? 16,
  },
  'mobile:gallery_categoryReadMoreFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) =>
      getStyleParamValue(gallery_categoryDescriptionReadMoreLinkFont)?.size ?? 16,
  },
  'mobile:gallery_categoryFilterAndSortButtonFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_filterOptionsFont)?.size ?? 16,
  },
  'mobile:gallery_categoryTitleFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryNameFont)?.size ?? 28,
  },
  gallery_categoryTreeTitleFont,
  gallery_categoryTreeTitleColor,
  gallery_categoryMenuTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleFont',
      },
    },
  },
  gallery_categoryMenuTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleColor',
      },
    },
  },
  gallery_filtersTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleFont',
      },
    },
  },
  gallery_filtersTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleColor',
      },
    },
  },
  gallery_categoryAndFilterNamesFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoryAndFilterNamesColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_filterOptionsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_filterOptionsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_selectedCategoryFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_selectedCategoryColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_categoryListHeaderFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: false,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: false,
        // },
      },
    },
  },
  gallery_categoryListHeaderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListViewMoreLessFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_categoryListViewMoreLessColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryHoverFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_categoryHoverColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_clearFiltersButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_clearFiltersButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_sortingLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_sortingLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_sortingFiltersTextColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_sortingFiltersTextColor',
      },
    },
  },
  gallery_sortingDefaultOption: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: 'gallerySortRecommended',
    }),
  },
  gallery_categoryMobileFilterAndSortButtonStyle: {
    type: StyleParamType.Number,
    getDefaultValue: () => FilterAndSortButtonStyle.TEXT,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls mobile filter and sort button border style: none (0) or all sides (1)',
      },
      defaultValue: {
        type: 'static',
        value: FilterAndSortButtonStyle.TEXT,
      },
    },
  },
  gallery_sortingAndFiltersFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_sortingAndFiltersButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_sortingAndFiltersButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_sortingAndFiltersButtonRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_quantityAndOptionsTextFont,
  gallery_priceBreakdownFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 12,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 12,
      },
    },
  },
  gallery_headerTextFont,
  gallery_titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', {
      size: 26,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-M',
        size: 26,
      },
    },
  },
  gallery_subtitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 15,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 15,
      },
    },
  },
  gallery_sortingFiltersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
      },
    },
  },
  gallery_sortingFiltersTextColor,
  gallery_outOfStockFontStyle,
  gallery_categoriesFilterAndSortButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoriesFilterAndSortButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_showCategories: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoriesTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showFiltersTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_enableStickySidebar: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_categoryListStrategy: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListStrategy.AUTOMATICALLY,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls which categories should be display in the category list: automatically show all the categories (0) or manually select categories (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListStrategy.AUTOMATICALLY,
      },
    },
  },
  gallery_categoryListItemsToShow: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListItemsToShow.OnlyNested,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls what should be shown in the horizontal category menu when there are no more subcategories: hide the category menu (0) or show same level categories (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListItemsToShow.OnlyNested,
      },
    },
  },
  gallery_categoryListLayout,
  gallery_showCategoryListShopAll: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) =>
      getStyleParamValue(gallery_categoryListLayout) === CategoryListLayout.Horizontal,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false, // TODO: editor 3 - verify fallback is correct for editor 3
      },
    },
  },
  gallery_categoryListHorizontalOverflow: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalOverflow.Scroll,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls horizontal category list overflow behavior: wrap to next line (0) or scroll horizontally (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListHorizontalOverflow.Scroll,
      },
    },
  },
  gallery_scrollNavigationIntervals: {
    type: StyleParamType.Number,
    getDefaultValue: () => ScrollNavigationIntervalsOption.SINGLE,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls scroll navigation intervals for slider gallery: single item per scroll (0) or multiple items per scroll (1)',
      },
      defaultValue: {
        type: 'static',
        value: ScrollNavigationIntervalsOption.SINGLE,
      },
    },
  },
  gallery_categoryListHorizontalSpaceBetween: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 8,
      },
    },
  },
  gallery_categoryListHorizontalButtonsVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 6,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 6,
      },
    },
  },
  gallery_categoryListHorizontalButtonsHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 12,
      },
    },
  },
  gallery_categoryListHorizontalWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  gallery_categoryListHorizontalPosition: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalPosition.AttachedToGallery,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls horizontal category list position: iniline with products gallery (0) or stretched at top (1)',
      },
      defaultValue: {
        type: 'static',
        value: CategoryListHorizontalPosition.AttachedToGallery,
      },
    },
  },
  gallery_categoryListHorizontalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ContentJustification.START,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: ContentJustification.START,
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_categoryListHorizontalHoverButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_categoryListHorizontalSelectedButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_categoryListHorizontalBaseButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListHorizontalHoverButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListHorizontalSelectedButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_categoryListHorizontalBaseButtonTextFont,
  gallery_categoryListHorizontalHoverButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonTextFont',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonTextFont',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderWidth,
  gallery_categoryListHorizontalHoverButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderWidth',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderWidth',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonCornerRadius,
  gallery_categoryListHorizontalHoverButtonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonCornerRadius),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonCornerRadius',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonCornerRadius),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonCornerRadius',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListHorizontalHoverButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_editorViewMode: {
    type: StyleParamType.Number,
    getDefaultValue: () => GalleryViewMode.EDITOR_EMPTY_STATE,
  },
  gallery_categoryListHorizontalUnderlineColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_categoryListHorizontalUnderlineWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderType,
  gallery_categoryListHorizontalHoverButtonBorderType: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderType),
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls horizontal category list hover button border style: none (0), bottom only (1), or all sides (2)',
      },
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderType',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderType: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderType),
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls horizontal category list selected button border style: none (0), bottom only (1), or all sides (2)',
      },
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderType',
      },
    },
  },
  gallery_noProductsMessageFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 20,
      },
    },
  },
  gallery_quickViewTextFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_ribbonBackground: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_ribbonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_sliderArrowsPlacement: {
    type: StyleParamType.Number,
    getDefaultValue: () => SliderArrowsPlacement.Outside,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'controls slider gallery navigation arrows placement: inside the slider (0) or outside the slider (1)',
      },
      defaultValue: {
        type: 'static',
        value: SliderArrowsPlacement.Outside,
      },
    },
  },
  gallery_sliderShowArrowsOnHoverOnly: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_sliderInfiniteLoop: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_sliderTeaserOffset: {
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'static',
        value: 40,
      },
    },
  },
  gallery_sliderShowTeaser: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showSliderNavigationArrows: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showSliderPaginationDots: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_productOptionButtonChoicesLimit: {
    type: StyleParamType.Number,
    getDefaultValue: () => 3,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 3,
      },
    },
  },
  gallery_stickySidebarDistanceFromTop: {
    type: StyleParamType.Number,
    getDefaultValue: () => 30,
    metadata: {
      type: StyleType.marginTop,
      defaultValue: {
        type: 'static',
        value: 30,
      },
    },
  },
  gallery_sliderArrowsSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 24,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
  },
  gallery_paginationDotsMarginTop: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.marginTop,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  gallery_paginationDotsAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.CENTER,
    }),
  },
  gallery_sliderNavigationArrowsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 16,
      },
    },
  },
  gallery_sliderArrowsContainerBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_sliderArrowsCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_arrowBackground, // legacy arrow color in slider gallery
  gallery_sliderArrowsOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_arrowBackground),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_arrowBackground',
      },
    },
  },
  gallery_nameAndPriceGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_quantityAndOptionsAdditionalSwatchesTextFont,
  gallery_quantityAndOptionsAdditionalSwatchesTextColor,
  gallery_addToCartButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_addToCartButtonTextColorDisabled: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_addToCartButtonBackgroundColorDisabled: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstSystemDisabledColor),
    },
  },
  gallery_imageCarouselArrowColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_appliedFiltersTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_titleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_subtitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_ribbonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_addToCartButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_imageCarouselArrowContainerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_imageCarouselArrowContainerBorder: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_headerTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_layoutVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_layoutHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_sliderScrollBehavior: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls slider gallery scroll behavior: snapping to items (0) or seamless scrolling (1)',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_appliedFiltersBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_appliedFiltersBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_appliedFiltersBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_appliedFiltersFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_sortingFiltersColorBackground: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },

  gallery_cardCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_imageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_titleTextColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_priceColor,
  gallery_salePriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceColor',
      },
    },
  },
  gallery_strikethroughPriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceColor',
      },
    },
  },
  gallery_priceBreakdownColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph3Color),
    },
  },
  gallery_discountNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_dividerLength: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  gallery_dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_outOfStockTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph3Color),
    },
  },
  gallery_outOfStockTextFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_outOfStockFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_outOfStockFontStyle',
      },
    },
  },
  gallery_quantityAndOptionsBackgroundColor,
  gallery_quantityAndOptionsTextColor,
  gallery_quantityAndOptionsBorderWidth,
  gallery_quantityAndOptionsBorderColor,
  gallery_quantityAndOptionsColorOptionsSize,
  gallery_quantityAndOptionsColorCornerRadius,
  gallery_productOptionsBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBackgroundColor),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBackgroundColor',
      },
    },
  },
  gallery_productQuantityBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBackgroundColor),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBackgroundColor',
      },
    },
  },
  gallery_productOptionsTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsTextColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsTextColor',
      },
    },
  },
  gallery_productQuantityTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsTextColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsTextColor',
      },
    },
  },
  gallery_productOptionsBorderWidth: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBorderWidth',
      },
    },
  },
  gallery_productQuantityBorderWidth: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBorderWidth',
      },
    },
  },
  gallery_productOptionsBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBorderColor),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBorderColor',
      },
    },
  },
  gallery_productQuantityBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsBorderColor),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsBorderColor',
      },
    },
  },
  gallery_productOptionsTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsTextFont',
      },
    },
  },
  gallery_productQuantityTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsTextFont',
      },
    },
  },
  gallery_productOptionsColorOptionsSize: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsColorOptionsSize),
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsColorOptionsSize',
      },
    },
  },
  gallery_productOptionsColorCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsColorCornerRadius),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsColorCornerRadius',
      },
    },
  },
  gallery_productOptionsAdditionalSwatchesTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_quantityAndOptionsAdditionalSwatchesTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsAdditionalSwatchesTextFont',
      },
    },
  },
  gallery_productOptionsAdditionalSwatchesTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) =>
      getStyleParamValue(gallery_quantityAndOptionsAdditionalSwatchesTextColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_quantityAndOptionsAdditionalSwatchesTextColor',
      },
    },
  },
  gallery_paginationActiveNumberTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_paginationNumbersTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_paginationNumbersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_loadMoreButtonbackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_loadMoreButtonborderSize: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '1px',
    }),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_loadMoreButtonbuttonWidth: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '22%',
    }),
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_loadMoreButtoncornersRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_loadMoreButtontextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
      },
    },
  },
  gallery_background: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_borderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_borderWidth: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '0',
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_headerTextAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.START,
    }),
    metadata: {
      type: StyleType.legacyFontStyleParamEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.START,
      },
    },
  },
  gallery_textualOptionType: {
    type: StyleParamType.Number,
    getDefaultValue: () => TextualOptionType.DROPDOWN,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls how textual product options are displayed: dropdown (1) or buttons (2)',
      },
      defaultValue: {
        type: 'static',
        value: TextualOptionType.DROPDOWN,
      },
    },
  },
  gallery_buttonOptionsHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 8,
      },
    },
  },
  gallery_buttonOptionsVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_buttonOptionBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_buttonOptionTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_buttonOptionFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_buttonOptionBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_buttonOptionBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_buttonOptionsGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 8,
      },
    },
  },
  gallery_buttonOptionBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_buttonOptionDisabledBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstSystemDisabledColor),
    },
  },
  gallery_buttonOptionDisabledTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstSystemDisabledColor),
    },
  },
  gallery_buttonOptionDisabledFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_buttonOptionSelectedBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_buttonOptionSelectedBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  gallery_buttonOptionSelectedTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_buttonOptionSelectedFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_buttonOptionHoverFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_buttonOptionHoverBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_buttonOptionHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_buttonOptionDisabledBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_buttonOptionHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_buttonSuffixFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
  },
  gallery_buttonSuffixTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_addToCartFlowType: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartFlowType.QUICK_VIEW,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description: 'controls add to cart flow type: quick view (0), quick add (1)',
      },
      defaultValue: {
        type: 'static',
        value: AddToCartFlowType.QUICK_VIEW,
      },
    },
  },
  gallery_quickAddShowProductOptionsTitles: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_quickAddShowAddToCartButton: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_quickAddLayoutOuterPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_quickAddLayoutInnerPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_quickAddContentGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 16,
      },
    },
  },
  gallery_quickAddContentAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue, isRTL}) => ({
      fontStyleParam: false,
      value: getFlexAlignment(getStyleParamValue(gallery_alignment).value, isRTL),
    }),
  },
  gallery_quickAddLayoutFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  gallery_quickAddLayoutBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  gallery_quickAddLayoutBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_quickAddLayoutBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
};
